import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useCreateDealer, useDealers, useDeleteDealer } from '../api/hooks';
import { offerDeactivateOrForceDelete } from '../utils/historyAwareDelete';
import { MAX_VIDEO_SECONDS } from '../components/investor/documentRules';
import PendingDocumentPicker from '../components/investor/PendingDocumentPicker';
import PasswordConfirmModal from '../components/common/PasswordConfirmModal';
import { usePasswordConfirm } from '../utils/confirmWithPassword';
import type { DealerListItem, DocumentType } from '../types';
import { apiDate, date, money } from '../utils/format';
import { autoSendAccountDetails, newAccountContact } from '../utils/quickShare';
import { paymentMethodOptions } from '../utils/paymentMethods';
import { useTranslation } from 'react-i18next';

const DEAL_TYPE_OPTIONS = [
  { value: 'PercentageBased', labelKey: 'forms.percentageHint' },
  { value: 'FixedReturn', labelKey: 'forms.fixedInvestmentHint' },
];

const MOBILE_PATTERN = /^03\d{2}-?\d{7}$/;
const CNIC_PATTERN = /^\d{5}-?\d{7}-?\d$/;

/** The Dealers list — mirrors the Investors page exactly: search, table, add, view, delete. */
export default function DealersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useDealers(search || undefined);
  const deleteDealer = useDeleteDealer();
  const passwordConfirm = usePasswordConfirm();
  const qc = useQueryClient();

  const onDelete = async (row: DealerListItem) => {
    if (!(await passwordConfirm.confirm(`delete ${row.dealerCode} — ${row.fullName}`))) return;

    Modal.confirm({
      title: `Delete ${row.dealerCode} — ${row.fullName}?`,
      content:
        'This only succeeds if this record has no transaction history. If they have any investments, withdrawals or profit payments, you will be offered the choice to deactivate instead.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDealer.mutateAsync({ id: row.id });
          message.success('My investment deleted.');
        } catch (err) {
          if (errorMessage(err).includes('has transaction history')) {
            offerDeactivateOrForceDelete({
              code: row.dealerCode,
              fullName: row.fullName,
              onDeactivate: async () => {
                await api.patch(`/dealers/${row.id}/status`, { status: 'Inactive' });
                qc.invalidateQueries({ queryKey: ['dealers'] });
              },
              onForceDelete: () => deleteDealer.mutateAsync({ id: row.id, force: true }),
              deactivatedMessage: 'My investment deactivated.',
              deletedMessage: 'My investment and all transaction history deleted.',
            });
          } else {
            Modal.error({ title: 'Could not delete this record', content: errorMessage(err) });
          }
        }
      },
    });
  };

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          My Investments
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Add my investment
        </Button>
      </Space>

      <Card>
        <Input.Search
          allowClear
          placeholder="Search by name, mobile, CNIC or code"
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
        />

        <Table<DealerListItem>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          scroll={{ x: 'max-content' }}
          onRow={(row) => ({ onClick: () => navigate(`/dealers/${row.id}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: t('table.name'), dataIndex: 'fullName' },
            { title: t('party.mobile'), dataIndex: 'mobile', render: (v: string | null) => v ?? '—' },
            {
              title: t('common.type'),
              dataIndex: 'dealType',
              render: (v: string) => (
                <Tag color={v === 'FixedReturn' ? 'geekblue' : 'gold'}>{v === 'FixedReturn' ? 'Fixed' : '% Based'}</Tag>
              ),
            },
            {
              title: t('common.status'),
              dataIndex: 'status',
              render: (v: string) => <Tag color={v === 'Active' ? 'green' : 'default'}>{v}</Tag>,
            },
            { title: t('finance.totalInvestment'), dataIndex: 'currentPrincipal', align: 'right', render: money },
            {
              title: t('finance.totalProfit'),
              dataIndex: 'outstandingProfit',
              align: 'right',
              render: (v: number) => <span style={{ color: v > 0 ? '#FF0000' : undefined }}>{money(v)}</span>,
            },
            {
              title: t('table.nextDueDate'),
              render: (_, row) =>
                !row.nextExpectedReturn ? (
                  '—'
                ) : (
                  <Space direction="vertical" size={0}>
                    <span>{date(row.nextExpectedReturn)}</span>
                    <Tag color={row.hasOverdue ? 'red' : row.daysToNextReturn <= 7 ? 'orange' : 'blue'}>
                      {row.daysToNextReturn < 0
                        ? `${Math.abs(row.daysToNextReturn)} days overdue`
                        : `${row.daysToNextReturn} days left`}
                    </Tag>
                  </Space>
                ),
            },
            {
              title: t('table.actions'),
              render: (_, row) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" onClick={() => navigate(`/dealers/${row.id}`)}>
                    View
                  </Button>
                  <Button danger type="link" size="small" onClick={() => onDelete(row)}>
                    Delete
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <CreateDealerModal open={open} onClose={() => setOpen(false)} />
      <PasswordConfirmModal state={passwordConfirm.state} />
    </>
  );
}

/**
 * Mirrors CreateInvestorModal field for field: identity, payment details, then documents, then an
 * optional initial investment. Documents can only be attached once the dealer row exists, so — same
 * as investors — they are uploaded right after creation, and a failed upload does not undo it.
 */
function CreateDealerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const createDealer = useCreateDealer();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<File | null>(null);
  const [cnicImage, setCnicImage] = useState<File | null>(null);
  const [agreementVideo, setAgreementVideo] = useState<File | null>(null);
  const [agreementImage, setAgreementImage] = useState<File | null>(null);
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const dealType = Form.useWatch('dealType', form) as string | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const resetAll = () => {
    form.resetFields();
    setPhoto(null);
    setCnicImage(null);
    setAgreementVideo(null);
    setAgreementImage(null);
    setTransactionImage(null);
  };

  /**
   * Attaches proof of the opening payment to the initial placement. The placement is created inside
   * the create call, so its id is read back here before the image is posted to it. A fixed-return
   * account books the opening amount on the fixed side, which is a different endpoint.
   */
  const uploadTransactionProof = async (dealerId: number, isFixed: boolean): Promise<boolean> => {
    if (!transactionImage) return true;

    try {
      const body = new FormData();
      body.append('file', transactionImage);

      if (isFixed) {
        const { data } = await api.get(`/dealers/${dealerId}/fixed`);
        const first = data?.investments?.[0];
        if (!first) return false;
        await api.post(`/dealers/${dealerId}/fixed/investments/${first.id}/transaction-image`, body, {
          headers: { 'Content-Type': undefined },
        });
      } else {
        const { data } = await api.get(`/dealers/${dealerId}/placements`);
        const first = data?.[0];
        if (!first) return false;
        await api.post(`/dealers/${dealerId}/placements/${first.id}/transaction-image`, body, {
          headers: { 'Content-Type': undefined },
        });
      }
      return true;
    } catch {
      return false;
    }
  };

  const uploadPending = async (dealerId: number): Promise<string[]> => {
    const pending: Array<[DocumentType, File | null]> = [
      ['photo', photo],
      ['cnic-image', cnicImage],
      ['agreement-video', agreementVideo],
      ['agreement-image', agreementImage],
    ];

    const failed: string[] = [];
    for (const [type, file] of pending) {
      if (!file) continue;
      try {
        const body = new FormData();
        body.append('file', file);
        await api.post(`/dealers/${dealerId}/documents/${type}`, body, {
          headers: { 'Content-Type': undefined },
        });
      } catch {
        failed.push(type);
      }
    }
    return failed;
  };

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      const result = await createDealer.mutateAsync({
        ...values,
        initialPlacementDate: apiDate(values.initialPlacementDate as dayjs.Dayjs | undefined),
        initialExpectedReturnDate: apiDate(values.initialExpectedReturnDate as dayjs.Dayjs | undefined),
        returnDate: apiDate(values.returnDate as dayjs.Dayjs | undefined),
      });

      setUploading(true);
      const failedUploads = await uploadPending(result.dealer.id);
      if (transactionImage && values.initialAmount) {
        const ok = await uploadTransactionProof(result.dealer.id, values.dealType === 'FixedReturn');
        if (!ok) failedUploads.push('transaction proof');
      }
      setUploading(false);
      if (failedUploads.length > 0) {
        message.warning(
          `My investment created, but these files did not upload: ${failedUploads.join(', ')}. Add them from the profile tab.`,
        );
      }

      // Fires by itself: the server sends when WhatsApp is configured, otherwise WhatsApp is
      // opened here with the message ready. Either way nothing has to be clicked in this app.
      autoSendAccountDetails(
        newAccountContact({
          partyType: 'Dealer',
          name: result.dealer.fullName,
          fatherName: result.dealer.fatherName,
          phone: result.dealer.mobile,
          cnic: result.dealer.cnic,
          code: result.dealer.dealerCode,
          dealType: (values.dealType as 'PercentageBased' | 'FixedReturn') ?? 'PercentageBased',
          initialAmount: Number(values.initialAmount ?? 0),
          returnAmount: values.returnAmount ? Number(values.returnAmount) : null,
          openedOn: apiDate(values.initialPlacementDate as dayjs.Dayjs | undefined) ?? '',
        }),
        result.whatsAppSent ?? false,
      );

      Modal.success({
        title: 'My investment created',
        width: 520,
        content: (
          <div>
            <p>
              Code / username: <strong>{result.username}</strong>
            </p>
            <p>
              Temporary password: <strong>{result.generatedPassword}</strong>
            </p>
            <p style={{ color: '#FF0000' }}>Save this password now — it will not be shown again.</p>
          </div>
        ),
      });
      resetAll();
      onClose();
      navigate(`/dealers/${result.dealer.id}`);
    } catch (err) {
      setUploading(false);
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal maskClosable={false}
      title={t('forms.addMyInvestment')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={createDealer.isPending || uploading}
      okText="Create"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
        <Form.Item name="fullName" label={t('party.fullName')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="fatherName" label="Father's name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile"
          label={t('party.mobile')}
          rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Enter an 11-digit mobile number' }]}
        >
          <Input placeholder="03001234567" />
        </Form.Item>
        <Form.Item
          name="cnic"
          label={t('party.cnic')}
          rules={[{ required: true }, { pattern: CNIC_PATTERN, message: 'Enter a 13-digit CNIC number' }]}
          help="Cannot be changed after the account is created."
        >
          <Input placeholder="3520212345671" />
        </Form.Item>
        <Form.Item name="address" label={t('party.address')} rules={[{ required: true }]} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="paymentMethod" label={t('finance.paymentMethod')} rules={[{ required: true }]}>
          <Select
            placeholder="Select how money moves to and from this investor"
            options={paymentMethodOptions}
          />
        </Form.Item>
        {paymentMethod !== 'Cash' && (
          <Form.Item name="accountNumber" label={t('finance.accountNumber')} rules={[{ required: true }]}>
            <Input placeholder="Account title, IBAN or wallet number" />
          </Form.Item>
        )}

        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          Documents
        </Divider>
        <PendingDocumentPicker
          type="photo"
          label={t('forms.profilePhoto')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={photo}
          onChange={setPhoto}
        />
        <PendingDocumentPicker
          type="cnic-image"
          label={t('party.cnicImage')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={cnicImage}
          onChange={setCnicImage}
        />
        <PendingDocumentPicker
          type="agreement-video"
          label={t('party.agreementVideo')}
          hint={`MP4 or WebM · max ${MAX_VIDEO_SECONDS} seconds · 25 MB`}
          file={agreementVideo}
          onChange={setAgreementVideo}
        />
        <PendingDocumentPicker
          type="agreement-image"
          label={t('party.agreementImage')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={agreementImage}
          onChange={setAgreementImage}
        />

        {/* Proof of the opening payment. Attached to the investment row created by the
            Amount field below, so it is only sent when an opening amount was entered. */}
        <PendingDocumentPicker
          type="transaction-image"
          label={t('finance.transactionProofOptional')}
          hint="Bank slip, JazzCash/EasyPaisa screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
          file={transactionImage}
          onChange={setTransactionImage}
        />

        <Divider orientation="left" plain>
          Initial investment
        </Divider>
        <Form.Item
          name="dealType"
          label={t('forms.investmentType')}
          rules={[{ required: true }]}
          initialValue="PercentageBased"
          style={{ marginTop: 8 }}
        >
          <Select options={DEAL_TYPE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))} />
        </Form.Item>
        <Form.Item name="initialAmount" label={t('common.amount')} rules={[{ required: true }]}>
          <InputNumber min={0.01} style={{ width: '100%' }} />
        </Form.Item>

        {dealType === 'FixedReturn' ? (
          <>
            <Form.Item
              name="returnAmount"
              label={t('forms.fixedReturnAmount')}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, value: number) =>
                    value === undefined || value === null || value >= (getFieldValue('initialAmount') ?? 0)
                      ? Promise.resolve()
                      : Promise.reject(new Error('The return cannot be less than the amount invested.')),
                }),
              ]}
              help="The whole sum coming back, capital included. No monthly rate applies."
            >
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="returnDate"
              label={t('forms.returnDueOn')}
              rules={[{ required: true }]}
              help="Becomes the default date for any further investments on this account."
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            name="initialProfitRate"
            label={t('finance.monthlyProfitRate')}
            help="Optional. Leave blank if the rate is not agreed yet — no profit accrues until it is set."
          >
            <InputNumber min={0} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="initialPlacementDate" label={t('finance.investmentDate')} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        {dealType !== 'FixedReturn' && (
          <Form.Item name="initialExpectedReturnDate" label={t('forms.profitExpectedOn')} help="Used for the countdown on the record's page.">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Form.Item name="notes" label={t('common.notes')}>
          <Input.TextArea rows={2} placeholder="Optional notes about this account" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
